import { Button } from '@/components/ui/primitives';
import {
    ContextNote,
    Dateline,
    FAQList,
    FeatureGrid,
    Hero,
    MarketingPage,
    PageFooter,
    StepList,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';

const PAGE = pageForRoute('/ipad-pdf-markup');

const STEPS = [
    {
        title: '1. Open a PDF drawing in Safari',
        body: 'Visit pdfmarkuptool.com in Safari on your iPad, choose Open a PDF drawing, and select a file from Files. The local PDF opens in the browser without an app install or account.',
        image: '/tool-shots/cloud.jpg',
        alt: 'A PDF construction drawing with a revision cloud',
    },
    {
        title: '2. Mark the sheet with Apple Pencil',
        body: 'Choose a cloud, callout, freehand, or text tool and mark the part of the drawing you want to discuss. Hold the Pencil still at the end of a freehand stroke to snap a recognized shape into a clean outline.',
        image: '/tool-shots/freehand.jpg',
        alt: 'Freehand markup drawn on a PDF sheet',
    },
    {
        title: '3. Write a note with Scribble',
        body: 'Open a text or callout field and handwrite your note with Scribble. Your handwriting is entered as text in the field.',
        image: '/tool-shots/callout.jpg',
        alt: 'A callout note beside a marked drawing location',
    },
    {
        title: '4. Move around the drawing with your fingers',
        body: 'Use two fingers to pinch and pan the sheet while keeping Apple Pencil for drawing. Move around without changing the selected markup tool.',
        image: '/tool-shots/text.jpg',
        alt: 'Text markup placed on a PDF drawing',
    },
    {
        title: '5. Export the marked-up drawing',
        body: 'Export an editable PDF to keep the markups editable, or a flattened PDF with marks painted onto the page. You can also export the markup list as a CSV file back to Files.',
        image: '/tool-shots/export-dialog.png',
        alt: 'PDF export options for editable and flattened markups',
    },
] as const;

const FEATURES = [
    {
        title: 'Smooth freehand strokes',
        body: 'Safari Pencil input uses coalesced samples and a short predicted tail to keep freehand drawing responsive.',
    },
    {
        title: 'Pencil hover preview',
        body: 'On iPads that support Pencil hover, preview where the stroke will land before touching the screen.',
    },
    {
        title: 'Hold to snap a shape',
        body: 'Rest the Pencil at the end of a freehand stroke to turn a recognized shape into a clean outline.',
    },
] as const;

const FAQ = [
    {
        question: 'Do I need to install an app?',
        answer: ['No. Open pdfmarkuptool.com in Safari and work with a PDF from Files in the browser.'],
    },
    {
        question: 'Does it work with Apple Pencil?',
        answer: ['Yes. Draw with Apple Pencil, use hover preview on supported iPads, and hold at the end of a stroke to snap a recognized shape.'],
    },
    {
        question: 'Are my drawings uploaded?',
        answer: ['A PDF opened from Files uses the local workflow in your browser; that workflow does not upload the drawing.'],
    },
    {
        question: 'Can I export the markups?',
        answer: ['Yes. Export an editable or flattened PDF, and export the markup list as a separate CSV file.'],
    },
] as const;

export function IpadPdfMarkupPage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical }}>
            <main>
                <Hero
                    eyebrow="PDF markup on iPad"
                    title="Mark up PDF drawings on iPad with Apple Pencil"
                    lead="Open a drawing in Safari, add Pencil markups, and export the finished PDF from your iPad."
                    cta={<Button href="/" variant="primary" size="lg">Open a PDF drawing</Button>}
                    chips={['Safari on iPad', 'Apple Pencil', 'PDF and CSV export']}
                />
                <ContextNote text={<>Bluebeam retired Revu for iPad and Vu for iPad from the App Store on December 31, 2025. <a href="https://support.bluebeam.com/legacy/resources/revu-for-ipad-eos-announcement.html">Read Bluebeam's EOS announcement</a>.</>} />
                <StepList
                    title="How to mark up a PDF on iPad"
                    lead="Use Safari, Apple Pencil, and Files to mark a drawing and export your comments."
                    steps={STEPS}
                />
                <FeatureGrid
                    sectionTitle="Apple Pencil tools for drawing"
                    sectionLead="Use Pencil input for freehand markup, with helpful previews and shape recognition."
                    cards={FEATURES}
                />
                <FAQList items={FAQ} />
                <Dateline reviewed={PAGE.reviewed} />
            </main>
            <PageFooter tagline="PDF drawing markup in Safari on iPad" />
        </MarketingPage>
    );
}
